import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import { DRIP_CAMPAIGN_DETAILS_ENDPOINT } from "../../config/urls";
import DripStep from "./DripStep"; // Ensure correct import path
import { ArrowBack } from "@mui/icons-material";
import BackButton from "../../common/back-button";
import { formatDateOnly } from "../../utils/dateUtils";

const DripMarketingCampaignDetails = ({ onNavigate }) => {
  const storedParams = localStorage.getItem("activePageParams");
  const params = storedParams ? JSON.parse(storedParams) : {};
  const dripCampaignId = params.dripCampaignId;

  const { getValidToken } = useAuth();

  const [dripCampaign, setDripCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchDripCampaign = async () => {
    try {
      setLoading(true);
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
      };

      const res = await fetch(
        `${DRIP_CAMPAIGN_DETAILS_ENDPOINT}${dripCampaignId}`,
        { headers },
      );

      if (!res.ok) throw new Error("Failed to fetch drip campaign");

      const data = await res.json();
      setDripCampaign(data);
    } catch (err) {
      console.error(err);
      setError("Unable to load drip campaign details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dripCampaignId) fetchDripCampaign();
  }, [dripCampaignId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!dripCampaign) return null;

  return (
    <Box sx={{ px: 3 }}>
      <BackButton
        onClick={() => {
          onNavigate("drip_marketing");
        }}
      />

      <h2 className="heading-title">Campaign Details</h2>

      <Divider sx={{ mb: 3 }} />

      <Stack spacing={1.5}>
        <Typography>
          <strong>Campaign Name:</strong> {dripCampaign?.name}
        </Typography>
        <Typography>
          <strong>Description:</strong> {dripCampaign?.description || "N/A"}
        </Typography>
        <Typography sx={{ textTransform: "capitalize" }}>
          <strong>Status:</strong> {dripCampaign?.status || "N/A"}
        </Typography>
        <Typography sx={{ textTransform: "capitalize" }}>
          <strong>Trigger Type:</strong>{" "}
          {dripCampaign?.trigger_type?.replaceAll("_", " ") || "N/A"}
        </Typography>
        <Typography>
          <strong>Start Date:</strong>{" "}
          {dripCampaign?.start_date
            ? formatDateOnly(dripCampaign.start_date)
            : "N/A"}
        </Typography>
        <Typography>
          <strong>End Date:</strong>{" "}
          {dripCampaign?.end_date
            ? formatDateOnly(dripCampaign.end_date)
            : "N/A"}
        </Typography>
        <Typography>
          <strong>Segment:</strong>{" "}
          <span
            onClick={() =>
              onNavigate("update_segment", {
                segmentId: dripCampaign?.segment?.id,
              })
            }
            style={{ color: "#1976d2", cursor: "pointer" }}
          >
            {dripCampaign?.segment?.name || "N/A"}
          </span>
        </Typography>
      </Stack>

      {dripCampaign?.drip_steps?.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography
            variant="h6"
            gutterBottom
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Drip Steps
          </Typography>

          <Stack spacing={2}>
            {dripCampaign.drip_steps.map((step, index) => (
              <DripStep
                key={index}
                step={{
                  stepName: step?.name,
                  delay: step?.delay_hours,
                  waitForUserReply: step?.wait_for_reply,
                  template: {
                    name: step?.template?.template_name,
                    buttons:
                      step?.template?.template_preview_json?.allButtons || [],
                  },
                  buttonReplyOptions: step?.button_reply_actions?.reduce(
                    (obj, item) => {
                      obj[item.button_id] = {
                        label: item.value.replaceAll("_", " "),
                        value: item.value,
                      };
                      return obj;
                    },
                    {},
                  ),
                  noReplyOption: {
                    label: step?.on_no_reply?.replaceAll("_", " "),
                    value: step?.on_no_reply,
                  },
                }}
                onDelete={null}
                readOnly
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default DripMarketingCampaignDetails;
